import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Preloader({ onComplete }) {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Simulate loading progress
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        // Accelerating progress curve
        const increment = prev < 60 ? 3 : prev < 85 ? 2 : 1;
        return Math.min(prev + increment, 100);
      });
    }, 30);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (progress === 100) {
      let outerTimer, innerTimer;
      outerTimer = setTimeout(() => {
        setIsVisible(false);
        innerTimer = setTimeout(() => {
          onComplete?.();
        }, 800); // Wait for exit animation to finish
      }, 400);
      return () => {
        clearTimeout(outerTimer);
        clearTimeout(innerTimer);
      };
    }
  }, [progress, onComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#9c1b42]"
          exit={{ 
            clipPath: 'inset(0 0 100% 0)',
          }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
        >
          {/* Background decorative grid */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
               style={{
                 backgroundImage: 'radial-gradient(#f4ceb3 0.5px, transparent 0.5px)',
                 backgroundSize: '24px 24px'
               }}
          />

          {/* Center Content */}
          <div className="relative flex flex-col items-center gap-8">
            {/* Brand Name */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col items-center"
            >
              <img
                src="/logo.svg"
                alt="Pomie Studio Logo"
                className="h-40 w-40 sm:h-48 sm:w-48 md:h-56 md:w-56 lg:h-64 lg:w-64 xl:h-72 xl:w-72 aspect-square object-contain shrink-0 mx-auto mb-4 rounded-full"
              />
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: '100%' }}
                transition={{ delay: 0.5, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="h-[1px] bg-[#f4ceb3]/30 mt-4"
              />
            </motion.div>

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-white text-sm md:text-base font-sans tracking-[0.3em] uppercase"
            >
              WE DESIGN EXPERIENCES
            </motion.p>

            {/* Progress Bar */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="w-48 md:w-64 flex flex-col items-center gap-3"
            >
              <div className="w-full h-[2px] bg-[#f4ceb3]/15 rounded-full overflow-hidden relative">
                <motion.div
                  className="h-full bg-[#f4ceb3] rounded-full relative preloader-shimmer"
                  style={{ width: `${progress}%` }}
                  transition={{ duration: 0.1 }}
                />
              </div>
              <span className="text-[#f4ceb3]/50 text-[10px] font-sans tracking-widest font-medium tabular-nums">
                {progress}%
              </span>
            </motion.div>
          </div>

          {/* Bottom decorative line */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.8, duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
            className="absolute bottom-12 left-12 right-12 h-[1px] bg-[#f4ceb3]/10 origin-left"
          />

          {/* Corner markers */}
          <span className="absolute top-8 left-8 text-[9px] font-sans tracking-widest text-[#f4ceb3]/30 uppercase">
            Est. 2026
          </span>
          <span className="absolute top-8 right-8 text-[9px] font-sans tracking-widest text-[#f4ceb3]/30 uppercase">
            ISB — RWP
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
